import { Kardex } from './supabase';

export type PeriodoCPP = 'hoy' | 'semana' | 'mes' | 'anio';

export interface FilaCPP {
  id: string;
  fecha: string;
  tipo: string;
  motivo: string;
  referencia: string | null;
  entradaFisica: number;
  salidaFisica: number;
  saldoFisico: number;
  cpp: number;
  entradaValor: number;
  salidaValor: number;
  saldoValor: number;
  esInicial?: boolean;
}

export interface EstadoCPP {
  stockInicial: number;
  entradas: number;
  salidas: number;
  saldoActual: number;
  cppVigente: number;
  valorInventario: number;
  filas: FilaCPP[];
  tieneMovimientos: boolean;
}

export const formatearBs = (valor: number): string => {
  const numero = Number.isFinite(valor) ? valor : 0;
  return `Bs. ${numero.toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatearNumero = (valor: number): string => {
  const numero = Number.isFinite(valor) ? valor : 0;
  return numero.toLocaleString('es-BO');
};

export const formatearCpp = (valor: number): string => {
  const numero = Number.isFinite(valor) ? valor : 0;
  return `Bs. ${numero.toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Devuelve el inicio (00:00) y fin (23:59:59.999) del período en hora local. */
export const calcularRango = (periodo: PeriodoCPP, ref: Date): { desde: Date; hasta: Date; label: string } => {
  const base = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());

  if (periodo === 'hoy') {
    const desde = new Date(base);
    const hasta = new Date(base);
    hasta.setHours(23, 59, 59, 999);
    return {
      desde,
      hasta,
      label: base.toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' }),
    };
  }

  if (periodo === 'semana') {
    const dia = base.getDay(); // 0=domingo
    const offset = dia === 0 ? -6 : 1 - dia; // lunes como inicio
    const desde = new Date(base);
    desde.setDate(base.getDate() + offset);
    const hasta = new Date(desde);
    hasta.setDate(desde.getDate() + 6);
    hasta.setHours(23, 59, 59, 999);
    return {
      desde,
      hasta,
      label: `Del ${desde.toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })} al ${hasta.toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' })}`,
    };
  }

  if (periodo === 'mes') {
    const desde = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const hasta = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
    hasta.setHours(23, 59, 59, 999);
    return {
      desde,
      hasta,
      label: desde.toLocaleDateString('es-BO', { month: 'long', year: 'numeric' }),
    };
  }

  // anio
  const desde = new Date(ref.getFullYear(), 0, 1);
  const hasta = new Date(ref.getFullYear(), 11, 31);
  hasta.setHours(23, 59, 59, 999);
  return { desde, hasta, label: `Año ${ref.getFullYear()}` };
};

/** Recalcula los saldos físicos y valorados de forma cronológica a partir de los movimientos. */
const calcularFilas = (movimientos: Kardex[]): FilaCPP[] => {
  const ordenados = [...movimientos].sort((a, b) => {
    const fa = new Date(a.created_at).getTime();
    const fb = new Date(b.created_at).getTime();
    if (fa !== fb) return fa - fb;
    return a.id.localeCompare(b.id);
  });

  let qty = 0;
  let cpp = 0;
  let valor = 0;

  return ordenados.map((mov) => {
    const cantidad = Number(mov.cantidad) || 0;
    let entradaFisica = 0;
    let salidaFisica = 0;
    let entradaValor = 0;
    let salidaValor = 0;

    if (mov.tipo === 'entrada') {
      const costo = Number(mov.costo_unitario ?? cpp) || 0;
      qty += cantidad;
      valor += cantidad * costo;
      cpp = qty > 0 ? valor / qty : 0;
      entradaFisica = cantidad;
      entradaValor = cantidad * costo;
    } else {
      const costo = cpp;
      qty -= cantidad;
      valor -= cantidad * costo;
      if (qty <= 0) {
        qty = 0;
        valor = 0;
        cpp = 0;
      }
      salidaFisica = cantidad;
      salidaValor = cantidad * costo;
    }

    return {
      id: mov.id,
      fecha: mov.created_at,
      tipo: mov.tipo,
      motivo: mov.motivo || '',
      referencia: mov.referencia ?? null,
      entradaFisica,
      salidaFisica,
      saldoFisico: qty,
      cpp,
      entradaValor,
      salidaValor,
      saldoValor: valor,
    };
  });
};

/** Construye el estado CPP de un producto para el período indicado. */
export const construirEstadoCPP = (
  movimientos: Kardex[],
  desde: Date,
  hasta: Date
): EstadoCPP => {
  const filas = calcularFilas(movimientos);
  const desdeMs = desde.getTime();
  const hastaMs = hasta.getTime();

  const anteriores = filas.filter((f) => new Date(f.fecha).getTime() < desdeMs);
  const ultimoAnterior = anteriores.length > 0 ? anteriores[anteriores.length - 1] : null;
  const stockInicial = ultimoAnterior ? ultimoAnterior.saldoFisico : 0;
  const cppInicial = ultimoAnterior ? ultimoAnterior.cpp : 0;
  const valorInicial = ultimoAnterior ? ultimoAnterior.saldoValor : 0;

  const enPeriodo = filas.filter((f) => {
    const t = new Date(f.fecha).getTime();
    return t >= desdeMs && t <= hastaMs;
  });

  const entradas = enPeriodo.reduce((s, f) => s + f.entradaFisica, 0);
  const salidas = enPeriodo.reduce((s, f) => s + f.salidaFisica, 0);

  const ultimo = enPeriodo.length > 0 ? enPeriodo[enPeriodo.length - 1] : null;
  const saldoActual = ultimo ? ultimo.saldoFisico : stockInicial;
  const cppVigente = ultimo ? ultimo.cpp : cppInicial;
  const valorInventario = ultimo ? ultimo.saldoValor : valorInicial;

  const filasSalida: FilaCPP[] = [
    {
      id: 'inicial',
      fecha: desde.toISOString(),
      tipo: 'inicial',
      motivo: 'Saldo al inicio del período',
      referencia: null,
      entradaFisica: 0,
      salidaFisica: 0,
      saldoFisico: stockInicial,
      cpp: cppInicial,
      entradaValor: 0,
      salidaValor: 0,
      saldoValor: valorInicial,
      esInicial: true,
    },
    ...enPeriodo,
  ];

  return {
    stockInicial,
    entradas,
    salidas,
    saldoActual,
    cppVigente,
    valorInventario,
    filas: filasSalida,
    tieneMovimientos: enPeriodo.length > 0,
  };
};